import React from 'react';
import {
  AppBar,
  Avatar,
  Button,
  Card,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Pagination,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { MedicalSuppliesService } from '../hooks';
import { extractApiErrorMessage, notifications } from '../utils';
import { useTranslation } from 'react-i18next';
import { ClearFilters, Loader, SearchIconButton } from '../elements';
import { LoaderSize } from '../constants/constants';
import { MedicalSupplyBottleViewModel } from '../models/injection';
import { AnimalType } from '../models/animal';
import CloseIcon from '@mui/icons-material/Close';
import { V4_DIALOG_MD, V4_SMALL_SCREEN } from '../theme/v4Compat';

interface MedicalSupplyBottleSelectModalProps {
  bottle: MedicalSupplyBottleViewModel | null;
  onChange: (supply: MedicalSupplyBottleViewModel | null) => void;
  selectorId?: string;
  disableAny?: boolean;
  disabled?: boolean;
  variant?: 'elevation' | 'outlined';
}

const MedicalSupplyBottleSelectModal = React.forwardRef((props: MedicalSupplyBottleSelectModalProps, ref) => {
  const { bottle, onChange, disableAny, variant } = props;

  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);

  const medicalSuppliesService = new MedicalSuppliesService();

  const [bottles, setBottles] = React.useState([] as MedicalSupplyBottleViewModel[]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const [text, setText] = React.useState('');
  const [supplyNumber, setSupplyNumber] = React.useState('');
  const [lotNumber, setLotNumber] = React.useState('');
  const [animalType, setAnimalType] = React.useState(0);

  React.useImperativeHandle(ref, () => ({
    openModal() {
      setOpen(true);
    }
  }));

  // pagination
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };
  // * pagination

  const itemsDisplayCount = 10;

  const loadMedicalSupplyBottles = async () => {
    setIsBusy(true);

    medicalSuppliesService
      .getMedicalSupplyBottles(page, itemsDisplayCount, supplyNumber, lotNumber, animalType, false, 'popularity-desc')
      .then((result) => {
        setBottles(result.data);
        setTotal(result.total);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleSelectorClick = () => {
    setOpen(true);
  };

  React.useEffect(() => {
    if (open && !loaded) {
      loadMedicalSupplyBottles();
    }
  }, [open, loaded]);

  React.useEffect(() => {
    if (!bottle) {
      setTextAny();
    } else {
      setTextBySupplyBottle(bottle);
    }
  }, [bottle]);

  const handleClose = () => {
    setOpen(false);
  };

  const setTextBySupplyBottle = (bottle: MedicalSupplyBottleViewModel) => {
    setText(`${bottle.medicalSupply.number} ${bottle.size} ml`);
  };

  const setTextAny = () => {
    setText(t('f-supp-bottle-any'));
  };

  const handleSupplyBottleSelect = (bottle: MedicalSupplyBottleViewModel) => {
    setTextBySupplyBottle(bottle);
    setOpen(false);
    onChange(bottle);
  };

  const handleSelectAny = () => {
    setOpen(false);
    setTextAny();
    onChange(null);
  };

  const clearFilters = () => {
    setSupplyNumber('');
    setLotNumber('');
    setPage(0);
    setAnimalType(0);
  };

  const renderFiltersSection = () => {
    return (
      <div className="p-2">
        <div className="-m-1 box-border flex w-full flex-wrap">
          <div className="box-border flex w-full items-center justify-center p-1 pb-[5px] min-[600px]:w-1/2">
            <div className="flex w-full items-center">
              <TextField
                id="supplyNumberField"
                label={t('f-supp-name')}
                variant="outlined"
                size="small"
                fullWidth
                value={supplyNumber}
                onChange={(e) => setSupplyNumber(e.target.value)}
              />
            </div>
          </div>

          <div className="box-border flex w-full items-center justify-center p-1 pb-[5px] min-[600px]:w-1/2">
            <div className="flex w-full items-center">
              <TextField
                id="lotNumberField"
                label={t('f-com-lot')}
                variant="outlined"
                size="small"
                fullWidth
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
              />
              <ClearFilters onClick={clearFilters} />
              <SearchIconButton onClick={() => setLoaded(false)} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSupplyBottleSecondaryText = (bottle: MedicalSupplyBottleViewModel) => {
    return `${bottle.lotNumber} ${bottle.treatmentNumber} ${AnimalType[bottle.animalType]}`;
  };

  return (
    <>
      <Card variant={variant} sx={{ cursor: 'pointer' }} elevation={1} onClick={handleSelectorClick}>
        <CardHeader
          // v4's -8px top margin on the action (v9 uses -4px), and the 7px padding this card always had.
          sx={{ p: '7px', '& .MuiCardHeader-action': { mt: -1 } }}
          avatar={<Avatar>{bottle ? text[0] : '?'}</Avatar>}
          action={
            <Tooltip arrow title={t('f-supp-bottle-any')}>
              <IconButton aria-label="settings">
                <Icon>{'medical_services'}</Icon>
              </IconButton>
            </Tooltip>
          }
          title={text}
          subheader={bottle ? renderSupplyBottleSecondaryText(bottle) : ''}
        />
      </Card>

      <Dialog
        onClose={handleClose}
        aria-labelledby="bottle-select-title"
        fullWidth
        fullScreen={fullScreen}
        maxWidth="md"
        sx={V4_DIALOG_MD}
        open={open}
      >
        {fullScreen && (
          <AppBar>
            <Toolbar>
              <div className="flex w-full justify-between">
                <div className="flex items-center">
                  <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                    <CloseIcon />
                  </IconButton>
                  <Typography variant="h6">{t('f-supp-bottle-select')}</Typography>
                </div>
              </div>
            </Toolbar>
          </AppBar>
        )}

        <DialogTitle id="bottle-select-title">{t('f-supp-bottle-select')}</DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {renderFiltersSection()}

          <Divider />

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <div className="flex justify-end">
              <div className="m-2 flex">
                <Chip label={total} variant="outlined" size="small" id="totalChip" />
                <div className="ml-2">
                  <Typography>{t('f-search-results')}</Typography>
                </div>
              </div>
            </div>
          )}

          {loaded && bottles && (
            <List dense>
              {bottles.map((bottle) => (
                <ListItemButton onClick={() => handleSupplyBottleSelect(bottle)} key={bottle.id}>
                  <ListItemAvatar>
                    <Avatar>{bottle.medicalSupply.number[0].toUpperCase() || '-'}</Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={`${bottle.medicalSupply.number} ${bottle.size} ml`}
                    secondary={renderSupplyBottleSecondaryText(bottle)}
                  />
                </ListItemButton>
              ))}

              <ListItem id="pagination-li" sx={{ justifyContent: 'center' }}>
                <Pagination
                  count={Math.floor(total / itemsDisplayCount)}
                  size="small"
                  color="primary"
                  showFirstButton
                  showLastButton
                  page={page}
                  onChange={handleChangePage}
                />
              </ListItem>
            </List>
          )}
        </DialogContent>

        <DialogActions>
          {!fullScreen && (
            <Button onClick={handleClose} color="primary">
              {t('m-global-close')}
            </Button>
          )}
          <Button onClick={handleSelectAny} color="primary" disabled={disableAny}>
            {t('f-supp-any')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default MedicalSupplyBottleSelectModal;
